import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { formatInTimeZone } from "date-fns-tz";
import {
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Button,
    Slide,
    Box,
    Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { hasPermission } from 'state/checkPermission';
import DeleteConfirm from 'components/DeleteConfirm';
import FlightStepper from "./FlightStepper";


const FlightStepperDialog = ({ open, onClose, flight, onProgressUpdate }) => {
    const user = useSelector((state) => state.user);
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const primaryDark = theme.palette.primary.dark;
    const neutralDark = theme.palette.neutral.dark;

    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [deleteType, setDeleteType] = useState(null); 


    const formatTZDate = (date, tz = "UTC", format = "HH:mm dd/MM/yy") => {
        if (!date) return "";
        try {
            return formatInTimeZone(date, tz, format);
        } catch {
            return "";
        }
    };

    const td = timeMode === "UTC"
        ? formatTZDate(flight?.fullAtd || flight?.fullEtd)
        : formatTZDate(flight?.fullAtdLocalBase || flight?.fullEtdLocalBase);
    
    // const ta = timeMode === "UTC"
    //     ? formatTZDate(flight?.fullAta || flight?.fullEta)
    //     : formatTZDate(flight?.fullAtaLocalBase || flight?.fullEtaLocalBase);

    
    const handleDeleteClick = () => {        
        setDeleteType('single');
        setConfirmDialogOpen(true);
    };

    const instanceId = flight?.instanceId;

    const handleDeleteInstance = async () => {
        try {
            if (!instanceId) {
                toast.error("No instance to delete!");
                return;
            }

            if (instanceId) {
                const url = `/tatinstances/delete/${instanceId}`;
                await api.delete(url);
                
                toast.success("Instance deleted successfully.");
                setConfirmDialogOpen(false);

                onProgressUpdate?.(null, 0, null);
                onClose();
            }
        } catch (error) {
            toast.error(error?.message || "Failed to delete TAT instance!");
        }
    };

    return (
        <Slide direction="up" in={open} mountOnEnter unmountOnExit timeout={220}>
            <Box>
                <Dialog
                    fullWidth
                    maxWidth="sm"
                    open={open}
                    onClose={onClose}
                    keepMounted
                    sx={{
                        "& .MuiDialog-paper": {
                            borderRadius: 1,
                            boxShadow: 3,
                            overflow: "hidden",
                        },
                    }}
                >               
                    <DialogTitle
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            pr: 2,
                            py: 1.5,
                            borderBottom: `1px solid ${neutralDark}`,
                        }}
                    >
                        <Typography fontWeight="bold" color={primaryDark}>
                            TURNAROUND TASKS
                        </Typography>
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    {flight && (
                        <Box
                            sx={{
                                borderBottom: `1px solid ${neutralDark}`,
                                backgroundColor: theme.palette.action.hover,
                                px: 2,
                                py: 1.2,
                            }}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                flexWrap="wrap"
                                spacing={1}
                            >
                                {/* Flight No */}
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography variant="body2">
                                        {flight.flightDesc}
                                    </Typography>                                
                                </Stack>

                                {/* Sector */}
                                <Typography variant="body2">                                    
                                    {flight.dep || "-"} → {flight.arr || "-"}
                                </Typography>

                                {/* Registration */}
                                <Typography variant="body2">
                                    {flight.flightReg || "-"}
                                </Typography>

                                {/* ETD */}
                                <Typography variant="body2">
                                    {td}
                                </Typography>
                            </Stack>
                        </Box>
                    )}

                    <DialogContent
                        dividers
                        sx={{
                            p: 2,
                            backgroundColor: theme.palette.background.paper,
                        }}
                    >
                        <FlightStepper 
                            flight={flight} 
                            onProgressUpdate={onProgressUpdate}
                        />
                    </DialogContent>
                   
                    <DialogActions
                        sx={{
                            justifyContent: "flex-end",                           
                            borderTop: `1px solid ${neutralDark}`,
                        }}
                    >
                        {instanceId && (
                            <Button 
                                variant="outlined" 
                                color="error" 
                                onClick={() => {
                                    if (!hasPermission(user?.permissions, "tatinstances", "delete")) return;
                                    handleDeleteClick();
                                }}
                            >
                                Delete
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={onClose}
                        >
                            CLOSE
                        </Button>
                    </DialogActions>
                </Dialog>

                <DeleteConfirm
                    open={confirmDialogOpen}
                    onClose={() => setConfirmDialogOpen(false)}
                    onConfirm={handleDeleteInstance}
                    deleteType={deleteType}
                />
            </Box>
        </Slide>
    );
};

export default FlightStepperDialog;
